import {
  ConjugationTable,
  ShownTranslation,
  TranslationLanguage,
  conjugateVerb,
  translationFor,
  verbSpecFromGenerated,
} from '@lingoping/core';
import { CHROMATIC_PALETTES } from '../theme/index.js';
import type { CloudDeckWord } from '../services/cloudDecks.js';

/**
 * The meaning (and example) of a deck word in the learner's translation language, English when
 * that language is missing. Null while the word has not been prepared (no library row yet).
 */
/**
 * The word a deck entry stands for: the library entry's dictionary form once the entry is
 * prepared ("Die Speisekarte" in a list is the noun "Speisekarte"), otherwise the text as written.
 */
export function deckWordLemma(word: CloudDeckWord): string {
  return word.word?.lemma || word.lemma;
}

export function deckWordMeaning(
  word: CloudDeckWord,
  language: TranslationLanguage
): ShownTranslation | null {
  const library = word.word;
  if (!library?.translation) return null;
  return translationFor(
    {
      back_text: library.translation,
      example_native: library.example_native ?? undefined,
      translations: library.translations ?? undefined,
    },
    language
  );
}

export function deckWordArticle(word: CloudDeckWord): string | null {
  return word.word?.linguistic_meta?.article ?? null;
}

/** The gender colours of the word's article (neutral for words without one). */
export function deckWordPalette(word: CloudDeckWord) {
  return CHROMATIC_PALETTES[word.word?.linguistic_meta?.gender ?? 'neutral'];
}

/** A verb's tense tables, from the library row. */
export function deckWordConjugation(word: CloudDeckWord): ConjugationTable | null {
  const library = word.word;
  if (library?.conjugation) return library.conjugation;
  const verb = library?.linguistic_meta?.verb;
  return library?.part_of_speech === 'verb' && verb
    ? conjugateVerb(verbSpecFromGenerated(deckWordLemma(word), verb))
    : null;
}
